import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouteHook } from '@shared/hooks';
import { AmountUtils, FormatUtils, PermissionUtils, RoutePaths } from '@shared/utils';
import { AccountType, type AccountResponse } from '@shared/api';
import { Table, TableCellType } from '@shared/widgets';

interface AccountsTableProps {
  accountType: AccountType;
  accounts: AccountResponse[];
}

export const AccountsTable = (props: AccountsTableProps) => {
  const { navigate } = useRouteHook();

  const { t } = useTranslation('accounts');

  const cardColumns = useMemo(
    () => [
      t('card.cardColumns.account'),
      t('card.cardColumns.currency'),
      t('card.cardColumns.product'),
      t('card.cardColumns.status'),
      t('card.cardColumns.limit'),
      t('card.cardColumns.balance'),
    ],
    [t],
  );
  const accountColumns = useMemo(
    () => [
      t('payment.accountColumns.accountName'),
      t('payment.accountColumns.product'),
      t('payment.accountColumns.account'),
      t('payment.accountColumns.currency'),
      t('payment.accountColumns.iban'),
      t('payment.accountColumns.status'),
      t('payment.accountColumns.balance'),
    ],
    [t],
  );
  const data = useMemo(() => props.accounts.filter((account) => account.type == props.accountType), [props.accounts, props.accountType]);

  if (props.accountType === AccountType.Card)
    return (
      <Table
        isScrollable
        columns={cardColumns}
        items={data.map((row) => [
          { type: TableCellType.Text, value: row.accountNumber },
          { type: TableCellType.Text, value: row.currency },
          { type: TableCellType.Text, value: row.productName },
          { type: TableCellType.Text, value: t(`statuses.account.${row.status}`, { ns: 'common' }).toUpperCase() },
          { type: TableCellType.Text, value: AmountUtils.transformToAmountWithCurrency(row.creditLimit ?? 0, row.currency) },
          {
            type: TableCellType.Text,
            value: PermissionUtils.viewAccountAvailableBalance(row.id)
              ? AmountUtils.transformToAmountWithCurrency(row.balance ?? 0, row.currency)
              : '****',
          },
        ])}
        onClick={(rowIndex) => navigate(RoutePaths.account.onNavigate(data[rowIndex].id))}
      />
    );
  return (
    <Table
      isScrollable
      columns={accountColumns}
      items={data.map((row) => [
        { type: TableCellType.Text, value: row.name || FormatUtils.formatAccountNumber(row.accountNumber) },
        { type: TableCellType.Text, value: row.productName },
        { type: TableCellType.Text, value: row.accountNumber },
        { type: TableCellType.Text, value: row.currency },
        { type: TableCellType.Text, value: row.iban },
        { type: TableCellType.Text, value: t(`statuses.account.${row.status}`, { ns: 'common' }).toUpperCase() },
        {
          type: TableCellType.Text,
          value: PermissionUtils.viewAccountAvailableBalance(row.id)
            ? AmountUtils.transformToAmountWithCurrency(row.availableBalance ?? 0, row.currency)
            : '****',
        },
      ])}
      onClick={(rowIndex) => navigate(RoutePaths.account.onNavigate(data[rowIndex].id))}
    />
  );
};
